import { V6MacroVersion, V6OfflineCause } from '../../interop/interfaces/v6-config.js';
import { isEmptyOrSpace } from '../ui/string-helper-functions.js';

/**
 * What version the V6 frame-config bootloader should load, and whether that's the live version or
 * a cached fallback used because the supplier is offline. Pure decision logic, kept apart from the
 * script-loading mechanics in v6config-loader.ts so it can be tested without a DOM/network harness.
 */
export type BootVersionOutcome =
  | { kind: 'live'; requiredVersion: string }
  | {
      kind: 'cache';
      requiredVersion: string;
      offlineCause: V6OfflineCause;
      message?: string;
      /**
       * False when the "offline" signal was actually a total local failure to reach our own
       * anonymous version-check endpoint (`dealerVersion === undefined`), not the server telling
       * us the supplier is down. The supplier's own state hasn't changed in that case - it's
       * usually WebModule itself restarting - so the caller must still boot on this outcome the
       * same way, but must NOT show the one-off offline notice for it.
       */
      confirmedOffline: boolean;
    }
  | { kind: 'blocked'; genuinelyOffline: boolean; offlineCause: V6OfflineCause; message?: string };

/**
 * Offline causes a cached version may be used for. The supplier being down (Connection) or mid
 * upgrade (Upgrading) is an outage the dealer had nothing to do with. VersionMismatch and
 * InvalidV6ClientCode are the supplier itself reporting a fault - a stale cached version would
 * paper over that fault rather than surface it, so those get no fallback (architect decision,
 * order 002 review, 2026-09-25).
 */
const CACHEABLE_OFFLINE_CAUSES: ReadonlySet<V6OfflineCause> = new Set([
  V6OfflineCause.Connection,
  V6OfflineCause.Upgrading
]);

/**
 * Decide what version to boot with given the live dealer-macro-version result and whatever was
 * last cached for this supplier.
 *
 * This is keyed on `online === false` with a cacheable `offlineCause` - NOT on an empty
 * `configVersion`. While WebModule itself stays up through an outage, the server keeps returning
 * its own last-known-good settings alongside `online: false` (TaskGetSuppliers.cs's
 * `FillWithLastGoodSettings`), so `configVersion` is normally NOT empty on an offline boot - only a
 * totally cold cache (nothing server-side or locally cached) leaves it empty. Checking emptiness
 * instead of `online` missed this common case entirely and took the `live` path while actually
 * offline.
 *
 * A cached (or server-last-good) version is only ever used as a fallback for a supplier that is
 * offline for a cacheable reason (see `CACHEABLE_OFFLINE_CAUSES`). A supplier that is online but
 * reports an empty/unusable configVersion is a real fault (@SUPOFF-BOOT-006), not an outage, and
 * must fail exactly as it does today rather than being masked by a stale cache.
 */
export function decideBootVersion(
  dealerVersion: V6MacroVersion | undefined,
  cached: V6MacroVersion | undefined
): BootVersionOutcome {
  // No response at all (the anonymous version check itself couldn't be reached) is as clear an
  // offline signal as the server explicitly reporting online: false - for choosing a version. It is
  // NOT the same as being told the supplier dropped: the supplier's own state hasn't changed, this
  // is us failing to ask (typically WebModule itself restarting), so `confirmedOffline` stays false
  // and the caller must not show the one-off offline notice for it (round 3 review).
  if (dealerVersion === undefined || dealerVersion.online === false) {
    const offlineCause = dealerVersion?.offlineCause ?? V6OfflineCause.Connection;
    const confirmedOffline = dealerVersion !== undefined;
    return decideOfflineBoot(
      offlineCause,
      dealerVersion?.offlineMessage,
      dealerVersion?.configVersion,
      cached,
      confirmedOffline
    );
  }

  // Online: live if it reports a usable version, otherwise a real fault (@SUPOFF-BOOT-006) - never
  // a cache candidate, however stale a version we might have lying around.
  const liveVersion = dealerVersion.configVersion ?? '';
  if (!isEmptyOrSpace(liveVersion)) {
    return { kind: 'live', requiredVersion: liveVersion };
  }
  return { kind: 'blocked', genuinelyOffline: false, offlineCause: V6OfflineCause.Connection, message: undefined };
}

/**
 * The supplier is offline (or unreachable). For a non-cacheable cause (VersionMismatch,
 * InvalidV6ClientCode, anything else) the server already handled this itself - it raises that
 * cause AFTER settings load, so the response still carries whatever configVersion it has. Boot on
 * that exactly as the app did before any of this cache-boot machinery existed: no offline-boot
 * notice, no `cache` outcome at all, just `live`. Only the LOCAL browser cache is refused for these
 * causes - if the server gave us nothing to boot with either, we're still blocked (round 3 review;
 * this was a regression in round 2 that would have locked the whole site out on a VersionMismatch
 * the server can already recover from on its own).
 *
 * For a cacheable cause, the server's own last-known-good `configVersion` wins when it has one; our
 * local browser cache is the fallback for when even that isn't available (e.g. WebModule itself
 * only just started and has no last-good settings recorded yet either).
 */
function decideOfflineBoot(
  offlineCause: V6OfflineCause,
  message: string | undefined,
  serverSuppliedVersion: string | undefined,
  cached: V6MacroVersion | undefined,
  confirmedOffline: boolean
): BootVersionOutcome {
  const hasServerVersion = !!serverSuppliedVersion && !isEmptyOrSpace(serverSuppliedVersion);

  if (!CACHEABLE_OFFLINE_CAUSES.has(offlineCause)) {
    if (hasServerVersion) {
      return { kind: 'live', requiredVersion: serverSuppliedVersion as string };
    }
    return { kind: 'blocked', genuinelyOffline: true, offlineCause, message };
  }

  const requiredVersion = hasServerVersion ? (serverSuppliedVersion as string) : cached?.configVersion;

  if (requiredVersion && !isEmptyOrSpace(requiredVersion)) {
    return { kind: 'cache', requiredVersion, offlineCause, message, confirmedOffline };
  }

  return { kind: 'blocked', genuinelyOffline: true, offlineCause, message };
}

/**
 * @SUPOFF-RETURN-001/002: once the supplier is confirmed back online, reconcile the version
 * actually running (loaded from a cache fallback) against the live version. A mismatch means a
 * script swap isn't safe, so the caller must force a full page reload instead.
 */
export function shouldReloadOnReconnect(loadedVersion: string | undefined, liveVersion: string | undefined): boolean {
  return !!loadedVersion && !!liveVersion && !isEmptyOrSpace(liveVersion) && liveVersion !== loadedVersion;
}
